<template>
  <div :class="`border border-gray p-4 rounded-md shadow-${editing ? 'xl' : 'sm'}`">
    <div class="flex justify-between items-center max-w-md mx-auto">
      <div
        v-if="!(creating || editing)"
        :class="`w-3 h-3 rounded-full bg-${isOnline ? 'green-500' : 'red-500'} mr-4`"
      />
      <div
        v-if="editing || creating"
        class="text-xl font-bold mr-4 w-64"
      >
        <label
          class="text-xl font-bold"
        >MAC
          <input
            ref="mac"
            v-model="localMac"
            name="mac"
            type="text"
            class="border rounded-sm p-1 focus:border-purple-500 w-full"
          >
        </label>
      </div>
      <div
        v-else
        class="text-xl font-bold mr-4 w-1/2"
      >
        {{ device.mac }}
      </div>
      <div
        v-if="editing || creating"
        class="flex space-x-2"
      >
        <a
          class="p-4 block m-0 hover:border-purple-500 hover:shadow-md rounded-full"
          @click="cancel"
        >
          <eva-icon
            name="close"
            fill="#282A43"
          />
        </a>
        <a
          class="p-4 block m-0 bg-green-500 rounded-full"
          @click="editDevice"
        >
          <eva-icon
            name="save-outline"
            fill="#ffffff"
          />
        </a>
      </div>
      <div
        v-else
        class="flex space-x-2"
      >
        <a
          class="p-4 block m-0 hover:border-purple-500 hover:shadow-md rounded-full"
          @click="editing=true"
        >
          <eva-icon
            name="edit-outline"
            fill="#282A43"
          />
        </a>
        <a
          class="p-4 block m-0 rounded-full"
          @click="deleteDevice"
        >
          <eva-icon
            name="trash-outline"
            fill="#FF7570"
          />
        </a>
      </div>
    </div>
    <div
      v-if="editing || creating"
      class="max-w-md mx-auto my-6"
    >
      <div class="text-xl w-64">
        <label
          class="text-xl font-bold"
        >Instancia
          <select
            v-model="localInstanceID"
            class="w-full bg-white border p-2 mb-1 focus:border-purple-500"
          >
            <option
              v-for="(instance, instanceID) in instancesInfo"
              :key="instanceID"
              :value="instanceID"
            >
              {{ instance.name }}
            </option>
          </select>
        </label>
      </div>
      <div class="text-xl w-64">
        <label
          class="text-xl font-bold"
        >Version
          <select
            v-model="localVersion"
            class="w-full bg-white border p-2 mb-1 focus:border-purple-500"
          >
            <option
              v-for="firmware in parsedFirmwareList"
              :key="firmware.id"
              :value="firmware.id"
            >
              {{ parseVersion(firmware.id) }}
            </option>
          </select>
        </label>
      </div>
    </div>
    <div
      v-else
      class="ml-6 mb-2"
    >
      <div
        :class="`${device.instanceID !== device.targetInstanceID ? 'italic opacity-50' : 'font-bold'} text-xl  w-64 truncate overflow-ellipsis mb-4`"
      >
        {{ instanceName }}
      </div>
      <div class="flex items-center">
        <div
          v-if="device.currentVersion !== device.targetVersion"
          class="mr-2"
        >
          <eva-icon
            class="animate-spin-slow"
            name="sync-outline"
            fill="#6C63FF"
          />
        </div>
        <div class="">
          {{ parseVersion(device.currentVersion) }} -> {{ parseVersion(device.targetVersion) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import * as axios from 'axios'
export default {
  props: {
    creating: {
      type: Boolean,
      default: false,
    },
    device: {
      type: Object,
      default: () => ({}),
    },
  },
  data () {
    return {
      editing: false,
      localMac: this.device?.mac || '',
      localInstanceID: this.device?.targetInstanceID || '',
      localVersion: this.device?.currentVersion || '',
    }
  },
  computed: {
    instanceName () {
      if (this.device.instanceID !== this.device.targetInstanceID) {
        return this.instancesInfo[this.device.targetInstanceID]?.name || ''
      }
      return this.instancesInfo[this.device.instanceID]?.name || ''
    },
    isOnline () {
      if (!this.device.heartbeat) return false
      return Date.now() - this.device.heartbeat.toMillis() < (1000 * 60 * 10)
    },
    ...mapGetters({
      parsedFirmwareList: 'admin/parsedFirmwareList',
    }),
    ...mapState({
      variants: state => state.admin.variants,
      instancesInfo: state => state.auth.instancesInfo,
    }),
  },
  watch: {
    editing (val) {
      if (val) {
        this.$nextTick(() => {
          this.$refs.mac.focus()
        })
      }
    },
  },
  methods: {
    cancel () {
      if (this.creating) this.$emit('close')
      else this.editing = false
    },
    async deleteDevice () {
      try {
        this.$f7.dialog.preloader('Eliminando...')
        const response = await axios({
          url: '/device/delete',
          method: 'POST',
          data: { device: { id: this.device.id } },
        })
        console.info(response)
      } catch (error) {
        console.error(error)
      } finally {
        this.$f7.dialog.close()
      }
    },
    async editDevice () {
      try {
        let data
        if (this.creating) {
          data = {
            device: {
              mac: this.localMac,
              targetInstanceID: this.localInstanceID,
              targetVersion: this.localVersion,
            },
          }
        } else {
          data = {
            device: {
              id: this.device.id,
              mac: this.localMac,
              targetInstanceID: this.localInstanceID,
              currentVersion: this.device.currentVersion,
              targetVersion: this.localVersion,
            },
          }
        }
        this.$f7.dialog.preloader('Guardando cambios...')
        const response = await axios({
          url: `/device/${this.creating ? 'create' : 'update'}`,
          method: 'POST',
          data,
        })
        console.info(response)
      } catch (error) {
        console.error(error)
      } finally {
        this.$f7.dialog.close()
        this.cancel()
      }
    },
    parseVersion (version) {
      if (this.variants) {
        const final = this.parsedFirmwareList.find(firmware => firmware.id === version)
        if (final) {
          const joinedVersion = final.version.split('-')
          return `${this.variants[joinedVersion[0]]}-${joinedVersion[1]}`
        }
      }
      return ''
    },
  },
}
</script>
